import { Button, CircularProgress, Dialog } from "@mui/material";
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  Elements,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { loadStripe, Stripe, StripeCardElementOptions } from "@stripe/stripe-js";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  useCompleteCmpMemberCardUpdateMutation,
  useStartCmpMemberCardUpdateMutation,
} from "../../../../graphql/types";
import {
  InvoiceDialogCloseButton,
  invoiceDesignFontFamily,
  invoiceDialogPaperSx,
  invoiceModalActionSx,
} from "../../Invoicing/InvoiceDesignSystem";
import { CALLER_HANDLED_USER_ERROR_CONTEXT } from "../../../../utils/apolloCallerContext";
import {
  buildStaffErrorFallback,
  getUserFacingErrorText,
  UserFacingError,
} from "../../../../utils/userFacingError";

// Monday D15/D18 (13242995751, 13244048118), Update Member Form (15)
// "Update Credit Card Information" pop-up, D:171300-176500 and D:220520.
// The design draws plain card inputs. Card number, expiration date and
// security code are Stripe card fields (Stripe iframes on the chamber's
// connected account), so card numbers never reach CMP. Name on card is only
// sent to Stripe as the card holder name.

export type UpdateCreditCardSavedResult = Readonly<{
  /** "Visa ending in 4242" for the success message highlight. */
  shortLabel: string | null;
}>;

type UpdateCreditCardDialogProps = Readonly<{
  chamberId: string;
  memberId: string;
  memberName: string;
  onClose: () => void;
  onSaved: (result: UpdateCreditCardSavedResult) => void;
  open: boolean;
}>;

type CardFieldKey = "number" | "expiry" | "cvc";

export const UPDATE_CREDIT_CARD_INCOMPLETE_MESSAGE = "Complete all card fields.";
export const UPDATE_CREDIT_CARD_NOT_CHANGED = "The saved card was not changed.";

export const getUpdateCreditCardSuccessDialog = (shortLabel: string | null) => ({
  title: "Credit card information updated successfully.",
  highlights: shortLabel ? [shortLabel] : [],
  note: "This card will be used for automatic membership renewals.",
});

const createCardUpdateOperationId = (): string =>
  `card-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;

const stripeByAccount = new Map<string, Promise<Stripe | null>>();
const getStripe = (publishableKey: string, connectedAccountId: string) => {
  const cacheKey = `${publishableKey}:${connectedAccountId}`;
  if (!stripeByAccount.has(cacheKey)) {
    stripeByAccount.set(
      cacheKey,
      Promise.resolve(loadStripe(publishableKey, { stripeAccount: connectedAccountId })).catch((error) => {
        stripeByAccount.delete(cacheKey);
        throw error;
      }),
    );
  }
  return stripeByAccount.get(cacheKey)!;
};

const labelClassName = "text-[11.5px] font-semibold tracking-[.05em] text-[#0A0A0A]";
const fieldLabelClassName = "text-[13px] text-[#0A0A0A]";
const fieldBoxClassName =
  "box-border w-full rounded-[8px] border border-[#9E9E9E] bg-white px-3 py-[9px] text-[14px] text-[#0A0A0A]";
const cardElementOptions: StripeCardElementOptions = {
  style: {
    base: {
      color: "#0A0A0A",
      fontFamily: '"DM Sans", sans-serif',
      fontSize: "14px",
      lineHeight: "20px",
      "::placeholder": { color: "#9E9E9E" },
    },
    invalid: { color: "#F21B1B" },
  },
};

// Shared plain-language text: server text without the Apollo wrapper, a
// network message, or the task fallback. Never the raw error message.
const getErrorMessage = (error: unknown, task = "save the card"): string =>
  getUserFacingErrorText(
    error,
    buildStaffErrorFallback(task, UPDATE_CREDIT_CARD_NOT_CHANGED)
  );

// Stripe writes card_error and validation_error text for the card holder
// ("Your card was declined."). Other Stripe errors can be technical.
const getStripeCardErrorText = (
  stripeError: { type?: string; message?: string } | undefined
): string =>
  stripeError?.message &&
  (stripeError.type === "card_error" || stripeError.type === "validation_error")
    ? stripeError.message
    : "The card was not accepted. Check the card number, expiration date, and security code, and try again.";

const CardFields = ({
  cardOnFile,
  clientSecret,
  memberName,
  onBusyChange,
  onCancel,
  onConfirmed,
}: Readonly<{
  cardOnFile: string;
  clientSecret: string;
  memberName: string;
  onBusyChange: (busy: boolean) => void;
  onCancel: () => void;
  onConfirmed: (setupIntentId: string) => Promise<void>;
}>) => {
  const stripe = useStripe();
  const elements = useElements();
  const [name, setName] = useState("");
  const [complete, setComplete] = useState<Record<CardFieldKey, boolean>>({ cvc: false, expiry: false, number: false });
  const [tried, setTried] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // Stripe confirmed the card but CMP did not save it yet: a retry only saves.
  const confirmedSetupIntentIdRef = useRef<string | null>(null);
  const incomplete = !name.trim() || !complete.number || !complete.expiry || !complete.cvc;
  const onFieldChange = (key: CardFieldKey) => (event: { complete: boolean }) =>
    setComplete((current) => ({ ...current, [key]: event.complete }));

  const save = async () => {
    setTried(true);
    setError(null);
    const card = elements?.getElement(CardNumberElement);
    if (!confirmedSetupIntentIdRef.current && (incomplete || !stripe || !card)) return;
    setSaving(true);
    onBusyChange(true);
    try {
      if (confirmedSetupIntentIdRef.current) {
        await onConfirmed(confirmedSetupIntentIdRef.current);
        return;
      }
      const result = await stripe!.confirmCardSetup(clientSecret, {
        payment_method: { billing_details: { name: name.trim() }, card: card! },
      });
      if (result.error || !result.setupIntent) {
        setError(`${getStripeCardErrorText(result.error)} ${UPDATE_CREDIT_CARD_NOT_CHANGED}`);
        return;
      }
      confirmedSetupIntentIdRef.current = result.setupIntent.id;
      await onConfirmed(result.setupIntent.id);
    } catch (saveError) {
      setError(getErrorMessage(saveError));
    } finally {
      setSaving(false);
      onBusyChange(false);
    }
  };

  return (
    <>
      <div className="flex min-h-0 flex-1 flex-col gap-[14px] overflow-y-auto px-[clamp(18px,3vw,28px)] pb-[clamp(12px,2vw,16px)] pt-[clamp(16px,3vw,22px)]">
        <h2 className="pr-9 text-[clamp(18px,2.6vw,21px)] font-bold text-[#0A0A0A]">
          Update Credit Card Information
        </h2>
        <div className="grid gap-4 border-b border-[#EDEFF2] pb-[10px] [grid-template-columns:repeat(auto-fit,minmax(160px,1fr))]">
          <div className="flex flex-col gap-[6px]">
            <div className={labelClassName}>MEMBER</div>
            <div className="text-[15px] font-bold text-[#0A0A0A]">{memberName}</div>
          </div>
          <div className="flex flex-col gap-[6px]">
            <div className={labelClassName}>CARD ON FILE</div>
            <div className="text-[15px] font-bold text-[#0A0A0A]">{cardOnFile}</div>
          </div>
        </div>
        <div className="flex flex-col gap-[10px]">
          <div className={labelClassName}>NEW CARD INFORMATION</div>
          <label className="flex flex-col gap-[6px]">
            <span className={fieldLabelClassName}>Name on card</span>
            <input
              autoComplete="cc-name"
              className={`${fieldBoxClassName} outline-none`}
              disabled={saving}
              onChange={(event) => setName(event.target.value)}
              placeholder="Full name as shown on card"
              value={name}
            />
          </label>
          <div className="flex flex-col gap-[6px]">
            <span className={fieldLabelClassName}>Card number</span>
            <div className={fieldBoxClassName} data-testid="card-number-field">
              <CardNumberElement onChange={onFieldChange("number")} options={{ ...cardElementOptions, disabled: saving, placeholder: "1234 1234 1234 1234" }} />
            </div>
          </div>
          <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-[14px]">
            <div className="flex flex-col gap-[6px]">
              <span className={fieldLabelClassName}>Expiration date</span>
              <div className={fieldBoxClassName}>
                <CardExpiryElement onChange={onFieldChange("expiry")} options={{ ...cardElementOptions, disabled: saving, placeholder: "MM / YY" }} />
              </div>
            </div>
            <div className="flex flex-col gap-[6px]">
              <span className={fieldLabelClassName}>Security code</span>
              <div className={fieldBoxClassName}>
                <CardCvcElement onChange={onFieldChange("cvc")} options={{ ...cardElementOptions, disabled: saving, placeholder: "CVC" }} />
              </div>
            </div>
          </div>
        </div>
        {tried && incomplete && !confirmedSetupIntentIdRef.current ? (
          <div role="alert" className="text-[14px] font-semibold text-[#F21B1B]">{UPDATE_CREDIT_CARD_INCOMPLETE_MESSAGE}</div>
        ) : null}
        {error ? (
          <div role="alert" className="text-[14px] font-semibold text-[#F21B1B]">{error}</div>
        ) : null}
      </div>
      <div className="flex flex-wrap items-center gap-3 bg-[#F4F8FF] px-[clamp(18px,3vw,28px)] py-[clamp(12px,2vw,16px)]">
        <Button
          disabled={saving || !stripe}
          onClick={save}
          startIcon={saving ? <CircularProgress color="inherit" size={16} /> : undefined}
          sx={{ ...invoiceModalActionSx({ backgroundColor: "#0091FF", hoverBackgroundColor: "#0077D6" }), flex: "1 1 260px", minHeight: 44 }}
          variant="contained"
        >
          Save Credit Card Information
        </Button>
        <Button
          disabled={saving}
          onClick={onCancel}
          sx={{ backgroundColor: "white", border: "1px solid #9E9E9E", borderRadius: "8px", color: "#0A0A0A", fontFamily: invoiceDesignFontFamily, fontSize: 14, minHeight: 44, px: 3, textTransform: "none" }}
        >
          Cancel
        </Button>
      </div>
    </>
  );
};

const UpdateCreditCardDialog = ({
  chamberId,
  memberId,
  memberName,
  onClose,
  onSaved,
  open,
}: UpdateCreditCardDialogProps) => {
  // Both errors show in this pop-up (startError, or the save error under the
  // card fields) instead of opening the error page.
  const [startCardUpdate, startState] = useStartCmpMemberCardUpdateMutation({
    context: CALLER_HANDLED_USER_ERROR_CONTEXT,
  });
  const [completeCardUpdate] = useCompleteCmpMemberCardUpdateMutation({
    context: CALLER_HANDLED_USER_ERROR_CONTEXT,
  });
  const [busy, setBusy] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);
  const startedForOpenRef = useRef(false);

  useEffect(() => {
    if (!open) {
      startedForOpenRef.current = false;
      setStartError(null);
      return;
    }
    if (startedForOpenRef.current) return;
    startedForOpenRef.current = true;
    startCardUpdate({ variables: { input: { chamberId, memberId, operationId: createCardUpdateOperationId() } } })
      .catch((error) =>
        setStartError(
          getUserFacingErrorText(
            error,
            buildStaffErrorFallback(
              "open the card form",
              "Close this window and try again."
            )
          )
        )
      );
  }, [chamberId, memberId, open, startCardUpdate]);

  const session = open && !startState.loading ? startState.data?.startCmpMemberCardUpdate : null;
  const stripePromise = useMemo(
    () => (session ? getStripe(session.publishableKey, session.connectedAccountId) : null),
    [session],
  );
  const close = () => {
    if (!busy) onClose();
  };

  return (
    <Dialog
      disableEscapeKeyDown={busy}
      fullWidth
      maxWidth={false}
      onClose={close}
      open={open}
      PaperProps={{ "aria-label": "Update Credit Card Information", sx: invoiceDialogPaperSx({ maxWidth: 700 }) }}
    >
      <div className="relative flex max-h-[92vh] min-h-0 flex-col bg-white" style={{ fontFamily: invoiceDesignFontFamily }}>
        <InvoiceDialogCloseButton ariaLabel="Close update credit card" disabled={busy} onClick={close} />
        {session && stripePromise ? (
          <Elements key={session.setupIntentId} stripe={stripePromise}>
            <CardFields
              cardOnFile={session.cardOnFile?.label || "No card on file"}
              clientSecret={session.clientSecret}
              memberName={memberName}
              onBusyChange={setBusy}
              onCancel={close}
              onConfirmed={async (setupIntentId) => {
                const result = await completeCardUpdate({ variables: { input: { chamberId, memberId, setupIntentId } } });
                const completed = result.data?.completeCmpMemberCardUpdate;
                if (!completed) throw new Error(`The card could not be saved. ${UPDATE_CREDIT_CARD_NOT_CHANGED}`);
                if (completed.outcome === "SUPERSEDED") {
                  throw new UserFacingError("A newer card was saved for this member first. Close the pop-up to see the card on file.");
                }
                onSaved({ shortLabel: completed.cardOnFile?.shortLabel || null });
              }}
            />
          </Elements>
        ) : (
          <div className="flex flex-col gap-[14px] px-[clamp(18px,3vw,28px)] pb-6 pt-[clamp(16px,3vw,22px)]">
            <h2 className="pr-9 text-[clamp(18px,2.6vw,21px)] font-bold text-[#0A0A0A]">Update Credit Card Information</h2>
            {startError ? (
              <div role="alert" className="text-[14px] font-semibold text-[#F21B1B]">{startError}</div>
            ) : (
              <div className="flex items-center gap-2 text-[14px] text-[#0A0A0A]">
                <CircularProgress size={16} /> Loading the secure card form...
              </div>
            )}
          </div>
        )}
      </div>
    </Dialog>
  );
};

export default UpdateCreditCardDialog;
